import {Injectable} from '@angular/core';
import {Observable} from 'rxjs';
import {Blueprint} from '../../models/blueprints/blueprint';
import {FabricService} from 'src/app/services/fabric/fabric.service';
import {toBoolean} from 'src/app/services/util/utils';
import {tap} from 'rxjs/operators';
import {PathToLiveTemplate} from 'src/app/models/blueprints/path-to-live-template';
import {BlueprintVersion} from '../../models/blueprints/blueprint-version';

const SERVICE_NAME = 'blueprint-resources';

@Injectable({
  providedIn: 'root'
})
export class BlueprintsService {

  constructor(private fabricService: FabricService) {

  }

  createBlueprint(blueprint: Blueprint): Observable<Blueprint> {
    return this.fabricService.invokeOperation<any>(SERVICE_NAME, 'post_blueprints', blueprint);
  }

  getBlueprint(id: string): Observable<Blueprint> {
    return this.fabricService.invokeOperation<any>(SERVICE_NAME, 'get_blueprints_id', {id})
      .pipe(tap(blueprint => this.convertModel(blueprint)));
  }

  createBlueprintVersion(id: string, solutionVersionId?: any, capabilities?: any, installDocRaw?: any): Observable<BlueprintVersion> {
    // eslint-disable-next-line max-len
    return this.fabricService.invokeOperation<any>(SERVICE_NAME, 'post_blueprints_id_version', {id, solutionVersionId, installDocRaw, capabilities});
  }

  getBlueprintVersion(id: string): Observable<BlueprintVersion> {
    return this.fabricService.invokeOperation<any>(SERVICE_NAME, 'get_blueprints_id_version', {id});
  }

  getBlueprintVersionById(blueprintId: string, id: string): Observable<BlueprintVersion> {
    return this.fabricService.invokeOperation<any>(SERVICE_NAME, 'get_blueprints_blueprintid_version_id', {blueprintId,id});
  }

  updateBlueprint(blueprint: Blueprint): any {
    return this.fabricService.invokeOperation<any>(SERVICE_NAME, 'put_blueprints_id', blueprint)
      .pipe(tap(updatedBlueprint => this.convertModel(updatedBlueprint)));
  }

  getBlueprints(tenantId?: string): Observable<Blueprint[]> {
    return this.fabricService.invokeOperation<any>(SERVICE_NAME, 'get_blueprints', tenantId ? {tenantId} : undefined)
      .pipe(tap(blueprints => blueprints.forEach(this.convertModel)));
  }

  getBlueprintsForSolution(solutionId: string): Observable<Blueprint[]> {
    return this.fabricService.invokeOperation<any>(SERVICE_NAME, 'get_blueprints', {solutionId})
      .pipe(tap(blueprints => blueprints.forEach(this.convertModel)));
  }

  getBlueprintsIdLatestVersion(id: string): Observable<BlueprintVersion> {
    return this.fabricService.invokeOperation<any>(SERVICE_NAME, 'get_blueprints_id_latest_version', {id})
      .pipe(tap(blueprint => this.convertModel(blueprint)));
  }

  deleteBlueprint(id: string): any {
    return this.fabricService.invokeOperation<any>(SERVICE_NAME, 'delete_blueprints_id', {id});
  }

  deleteBlueprintVersionById(blueprintId: string, id: string): Observable<BlueprintVersion> {
    return this.fabricService.invokeOperation<any>(SERVICE_NAME, 'delete_blueprints_blueprintid_version_id', {blueprintId,id});
  }

  getPathToLiveTemplates(): Observable<PathToLiveTemplate[]> {
    // Convert boolean variables since Fabric returns strings
    return this.fabricService.invokeOperation<any>(SERVICE_NAME, 'get_blueprints_path-to-live-templates')
      .pipe(tap(pathToLiveTemplates => pathToLiveTemplates.forEach(pathToLiveTemplate => {
        pathToLiveTemplate.configuration.forEach(configuration => configuration.editable = toBoolean(configuration.editable));
      })));
  }

  /**
   * Workaround to convert model's boolean values since fabric returns them as strings
   *
   * @param blueprint model
   */
  private convertModel(blueprint: Blueprint): void {
    blueprint.demo = toBoolean(blueprint.demo);
    if (blueprint.pathToLiveConfiguration) {
      blueprint.pathToLiveConfiguration.forEach(pathToLiveConfiguration => {
        pathToLiveConfiguration.editable = toBoolean(pathToLiveConfiguration.editable);
      });
    }
  }

}
